import { isEmptyPlainObject } from '@sortlist-frontend/utils';
import { useQuery } from '@tanstack/react-query';

import { api } from '_core/api/api-sl-base';
import { AgencyClaimFormValues } from '_features/agency/claim/agency-claim-validation';
import { LocaleParams } from '_types/generic';

export type SkillLookupData = {
  name: string;
};

export type Language = {
  name: string;
  code: string;
};

const url = {
  agencyClaimStore: (claimSessionId: string) => `/api/agency-claim/store/${claimSessionId}`,
  agencyClaimCacheStore: (claimSessionId: string) => `/api/agency-claim/cache/${claimSessionId}`,
};

export const agencyClaimRepo = {
  /**
   * Fetch the cached agency data.
   */
  loadAgencyData: async (
    params: { claimSessionId: string } & LocaleParams,
  ): Promise<{ data: Partial<AgencyClaimFormValues>; metadata: Partial<LocaleParams> } | null> => {
    return api.get(url.agencyClaimStore(params.claimSessionId)).then((resp) => {
      return isEmptyPlainObject(resp.data) ? null : resp.data;
    });
  },

  /**
   * Save the AgencyClaimFormValues. This triggers validation and the whole flow for creating a user, sending confirmation
   * emails, etc.
   */
  saveAgencyClaimForm: async (
    claimSessionId: string,
    values: AgencyClaimFormValues,
    extraParams: { locale: string } & LocaleParams,
  ): Promise<any> => {
    return api.put(url.agencyClaimStore(claimSessionId), {
      data: values,
      metadata: { locale: extraParams.locale || 'en' },
    });
  },

  /**
   * Cache only the AgencyClaimFormValues. The endpoint does not do any validation --
   * except for valid json and removes keys not defined in the AgencyClaimFormValues. Also no agency is created here.
   */
  cacheAgencyClaimForm: async (claimSessionId: string, values: AgencyClaimFormValues): Promise<any> => {
    return api.put(url.agencyClaimCacheStore(claimSessionId), values);
  },
};

export const useLoadAgencyData = (claimSessionId: string) => {
  return useQuery({
    queryKey: [url.agencyClaimStore(claimSessionId)],
    queryFn: () => {
      return agencyClaimRepo.loadAgencyData({ claimSessionId });
    },
  });
};
